// src/components/layout/RouteErrorBoundary.jsx
// Catches render errors and failed lazy-route imports. React caches a
// rejected lazy() import permanently, so the only recovery for a stale chunk
// is a reload; the boundary resets itself when the route changes.

import { Component } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isChunkLoadError } from '@/utils/staleChunk';

class Boundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[RouteErrorBoundary]', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadError(error);
    return (
      <div className={`${this.props.fullPage ? 'min-h-screen' : 'min-h-[300px]'} flex items-center justify-center p-4`}>
        <div className="bg-white rounded-lg shadow max-w-md w-full p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {stale ? 'A new version is available' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {stale
              ? 'This page could not be loaded because the app was updated. Reload to get the latest version.'
              : 'An unexpected error occurred while loading this page.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
          >
            Reload
          </button>
          {!this.props.fullPage && (
            <Link to="/" className="block mt-3 text-sm text-primary hover:text-primary/80">
              Go to home
            </Link>
          )}
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children, fullPage = false }) {
  const { pathname } = useLocation();
  return (
    <Boundary resetKey={pathname} fullPage={fullPage}>
      {children}
    </Boundary>
  );
}
